/**
 * Toast Component
 * Toast notifications for user feedback
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { Button } from './Button';
import { toastBus } from './toastBus';

const toastVariants = cva(
  'relative flex items-center justify-between w-full max-w-sm p-4 mb-4 text-sm rounded-lg shadow-lg border',
  {
    variants: {
      variant: {
        default: 'bg-white text-gray-900 border-gray-200',
        success: 'bg-success-50 text-success-800 border-success-200',
        error: 'bg-error-50 text-error-800 border-error-200',
        warning: 'bg-warning-50 text-warning-800 border-warning-200',
        info: 'bg-primary-50 text-primary-800 border-primary-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface ToastProps extends VariantProps<typeof toastVariants> {
  id: string;
  title?: string;
  description: string;
  duration?: number;
  onClose?: (id: string) => void;
  action?: {
    label: string;
    onClick: () => void;
  };
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
}

export function Toast({
  id,
  title,
  description,
  variant,
  duration = 5000,
  onClose,
  action,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.(id);
    }, 300);
  }, [id, onClose]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-error-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-warning-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
        return (
          <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        toastVariants({ variant }),
        'transform transition-all duration-300 ease-in-out',
        isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      )}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          {title && (
            <div className="font-medium mb-1">
              {title}
            </div>
          )}
          <div className="text-sm">
            {description}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2 ml-4">
        {action && (
          <Button
            variant="ghost"
            size="sm"
            onClick={action.onClick}
            className="text-xs"
          >
            {action.label}
          </Button>
        )}
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 rounded-md hover:bg-black hover:bg-opacity-10 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Toast Container Component
export interface ToastContainerProps {
  toasts: ToastProps[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  const portalRoot = document.getElementById('toast-root') || document.body;

  return createPortal(
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={onRemove}
        />
      ))}
    </div>,
    portalRoot
  );
}

// Toast Hook for easy usage
export function useToast() {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  useEffect(() => {
    const unsub = toastBus.subscribe(setToasts as any);
    return () => unsub();
  }, []);

  const toast = {
    success: (description: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'variant'>>) =>
      toastBus.success(description, options as any),
    error: (description: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'variant'>>) =>
      toastBus.error(description, options as any),
    warning: (description: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'variant'>>) =>
      toastBus.warning(description, options as any),
    info: (description: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'variant'>>) =>
      toastBus.info(description, options as any),
    default: (description: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'variant'>>) =>
      toastBus.show(description, 'default', options as any),
  };

  const removeToast = (id: string) => toastBus.remove(id);

  // Backward compatibility method
  const showToast = (message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    toastBus.show(message, variant);
  };

  return {
    toasts,
    toast,
    removeToast,
    showToast,
  };
}
